import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'shared/helpers/dayjs';
import { HeatmapCell, useNoteHeatmap } from 'dashboard/hooks/useNoteHeatmap';
import {
  buildHeatGradient,
  getHeatColour,
  getHeatScaleMax,
} from 'dashboard/helpers/heatmapColours';

import * as OverviewStyled from 'dashboard/styles/Overview.style';
import * as RechartsStyled from 'dashboard/styles/Recharts.style';
import * as Styled from 'dashboard/styles/Heatmap.style';
import * as Text from 'theme/components/Text.style';

const WEEKDAYS = [1, 2, 3, 4, 5];
const FIRST_HOUR = 7;
const LAST_HOUR = 17;
const HOURS = Array.from(
  { length: LAST_HOUR - FIRST_HOUR + 1 },
  (_, index) => FIRST_HOUR + index
);
const MINIMUM_RANGE_DAYS = 7;
const NO_CELLS: HeatmapCell[] = [];
const TOOLTIP_OFFSET = 8;
const MAX_LISTED_DATES = 3;

interface ActiveCell {
  hour: number;
  weekday: number;
  count: number;
  isFuture: boolean;
  left: number;
  top: number;
}

interface HeatmapSectionProps {
  queryParams: string;
  fromDate: string;
  toDate: string;
}

export default function HeatmapSection({
  queryParams,
  fromDate,
  toDate,
}: HeatmapSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageDashboard');
  const { data, isSuccess } = useNoteHeatmap(queryParams);
  const allCells = data ?? NO_CELLS;

  const rangeDays = dayjs(toDate).diff(dayjs(fromDate), 'day') + 1;
  const hasEnoughRange = rangeDays >= MINIMUM_RANGE_DAYS;

  const visibleCells = useMemo(
    () =>
      allCells.filter(
        (cell) =>
          WEEKDAYS.includes(cell.weekday) &&
          cell.hour >= FIRST_HOUR &&
          cell.hour <= LAST_HOUR
      ),
    [allCells]
  );

  const { countByKey, maxCount } = useMemo(
    () => ({
      countByKey: new Map(
        visibleCells.map((cell) => [`${cell.hour}-${cell.weekday}`, cell.count])
      ),
      maxCount: visibleCells.reduce(
        (max, cell) => Math.max(max, cell.count),
        0
      ),
    }),
    [visibleCells]
  );

  const pastSlots = useMemo(() => {
    const now = dayjs();
    const slots = new Set<string>();
    const end = dayjs(toDate);
    const lastDay = (end.isAfter(now) ? now : end).startOf('day');
    for (
      let day = dayjs(fromDate).startOf('day');
      !day.isAfter(lastDay);
      day = day.add(1, 'day')
    ) {
      HOURS.forEach((hour) => {
        if (day.hour(hour).isBefore(now)) slots.add(`${hour}-${day.day()}`);
      });
    }
    return slots;
  }, [fromDate, toDate]);

  const datesByWeekday = useMemo(() => {
    const dates = new Map<number, string[]>();
    for (
      let day = dayjs(fromDate).startOf('day');
      !day.isAfter(dayjs(toDate), 'day');
      day = day.add(1, 'day')
    ) {
      dates.set(day.day(), [
        ...(dates.get(day.day()) ?? []),
        day.format('MMM D'),
      ]);
    }
    return dates;
  }, [fromDate, toDate]);

  const formatWeekdayDates = (weekday: number) => {
    const dates = datesByWeekday.get(weekday) ?? [];
    if (dates.length <= MAX_LISTED_DATES) return dates.join(', ');
    return `${dates[0]} – ${dates[dates.length - 1]}`;
  };

  const layoutRef = useRef<HTMLDivElement>(null);
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);

  if (!isSuccess) return null;

  const scaleMax = getHeatScaleMax(maxCount);

  const formatHour = (hour: number) =>
    dayjs().hour(hour).minute(0).format('h A');

  const formatHourRange = (hour: number) =>
    t('heatmap.hourRange', {
      start: formatHour(hour),
      end: formatHour(hour + 1),
    });

  const activateCell = (
    element: HTMLElement,
    cell: Omit<ActiveCell, 'left' | 'top'>
  ) => {
    const layoutRect = layoutRef.current?.getBoundingClientRect();
    if (!layoutRect) return;
    const cellRect = element.getBoundingClientRect();
    setActiveCell({
      ...cell,
      left: cellRect.left - layoutRect.left + cellRect.width / 2,
      top: cellRect.bottom - layoutRect.top + TOOLTIP_OFFSET,
    });
  };

  const getCellState = (hour: number, weekday: number) => {
    if (!activeCell) return 'idle';
    return activeCell.hour === hour && activeCell.weekday === weekday
      ? 'active'
      : 'dimmed';
  };

  return (
    <OverviewStyled.SectionContainer>
      <OverviewStyled.SectionHeader>
        <Text.H5>{t('heatmap.title')}</Text.H5>
      </OverviewStyled.SectionHeader>

      <OverviewStyled.Card>
        {hasEnoughRange && visibleCells.length > 0 ? (
          <Styled.HeatmapLayout ref={layoutRef}>
            <Styled.GridScroller>
              <Styled.Grid $columns={WEEKDAYS.length}>
                {HOURS.map((hour) => (
                  <React.Fragment key={hour}>
                    <Styled.RowLabel>{formatHour(hour)}</Styled.RowLabel>
                    {WEEKDAYS.map((weekday) => {
                      const count = countByKey.get(`${hour}-${weekday}`) ?? 0;
                      const isFuture = !pastSlots.has(`${hour}-${weekday}`);
                      const cell = { hour, weekday, count, isFuture };
                      return (
                        <Styled.Cell
                          key={weekday}
                          tabIndex={0}
                          aria-label={`${dayjs()
                            .day(weekday)
                            .format('dddd')}, ${formatHourRange(hour)}: ${t(
                            'urgency.signals',
                            { count }
                          )}`}
                          $background={getHeatColour(count / scaleMax)}
                          $isFuture={isFuture}
                          $state={getCellState(hour, weekday)}
                          onMouseEnter={(event) =>
                            activateCell(event.currentTarget, cell)
                          }
                          onFocus={(event) =>
                            activateCell(event.currentTarget, cell)
                          }
                          onMouseLeave={() => setActiveCell(null)}
                          onBlur={() => setActiveCell(null)}
                        >
                          <Styled.CellValue>{count}</Styled.CellValue>
                        </Styled.Cell>
                      );
                    })}
                  </React.Fragment>
                ))}

                <span />
                {WEEKDAYS.map((weekday) => (
                  <Styled.ColumnLabel key={weekday}>
                    {dayjs().day(weekday).format('ddd').toUpperCase()}
                  </Styled.ColumnLabel>
                ))}
              </Styled.Grid>
            </Styled.GridScroller>

            {activeCell && (
              <Styled.CellTooltip
                role="tooltip"
                style={{ left: activeCell.left, top: activeCell.top }}
              >
                <RechartsStyled.TooltipTitle>
                  {formatHourRange(activeCell.hour)}
                </RechartsStyled.TooltipTitle>
                <RechartsStyled.TooltipSubtitle>
                  {dayjs().day(activeCell.weekday).format('dddd')}
                </RechartsStyled.TooltipSubtitle>
                <RechartsStyled.TooltipSubtitle>
                  {formatWeekdayDates(activeCell.weekday)}
                </RechartsStyled.TooltipSubtitle>
                <RechartsStyled.TooltipSubtitle>
                  {activeCell.isFuture
                    ? t('heatmap.notYet')
                    : t('urgency.signals', { count: activeCell.count })}
                </RechartsStyled.TooltipSubtitle>
              </Styled.CellTooltip>
            )}

            <Styled.Legend>
              <Styled.LegendBarColumn>
                <Styled.LegendMaxTick>{scaleMax}</Styled.LegendMaxTick>
                <Styled.LegendScale
                  $verticalGradient={buildHeatGradient('to top')}
                  $horizontalGradient={buildHeatGradient('to right')}
                />
                <Styled.LegendMinTick>0</Styled.LegendMinTick>
              </Styled.LegendBarColumn>
              <Styled.LegendTitle>{t('heatmap.legend')}</Styled.LegendTitle>
            </Styled.Legend>
          </Styled.HeatmapLayout>
        ) : (
          <RechartsStyled.ChartEmptyCentered>
            <RechartsStyled.ChartEmptyCard>
              <RechartsStyled.ChartEmptyTextContent>
                <RechartsStyled.ChartEmptyTitle>
                  {hasEnoughRange
                    ? t('heatmap.noData')
                    : t('heatmap.notEnoughRange')}
                </RechartsStyled.ChartEmptyTitle>
                <RechartsStyled.ChartEmptyDescription>
                  {hasEnoughRange
                    ? t('heatmap.noDataDescription')
                    : t('heatmap.notEnoughRangeDescription')}
                </RechartsStyled.ChartEmptyDescription>
              </RechartsStyled.ChartEmptyTextContent>
            </RechartsStyled.ChartEmptyCard>
          </RechartsStyled.ChartEmptyCentered>
        )}
      </OverviewStyled.Card>
    </OverviewStyled.SectionContainer>
  );
}
